import { formatNumber } from "../../components/charts/formatters"
import { getPulsePreviousDate, socialNetworkTypeIdToLabel } from "./inboxHealthReport"
import {
  catalogTypeIdFromKey,
  PULSE_PLATFORM_ICON_PATHS,
  socialNetworkTypeIdToDashboardPlatformKey,
} from "./pulseCardReport"

export function getDailyPostStatsPreviousDate(currentDate = new Date()) {
  return getPulsePreviousDate(currentDate, 1)
}

function pickPostStatNumber(row, keys) {
  if (!row || typeof row !== "object") return 0
  for (const key of keys) {
    const raw = row[key]
    if (raw == null || raw === "") continue
    const n = Number(raw)
    if (!Number.isNaN(n)) return n
  }
  return 0
}

function rowHasKey(row, keys) {
  if (!row || typeof row !== "object") return false
  return keys.some((key) => Object.prototype.hasOwnProperty.call(row, key))
}

/** Accept the new object payload or a legacy array / wrapped list. */
export function normalizeDailyPostStatsPayload(data) {
  if (Array.isArray(data)) return data
  if (data && typeof data === "object") {
    if (
      rowHasKey(data, [
        "publishedCount",
        "published_count",
        "currentlyLiveCount",
        "currently_live_count",
        "scheduledLaterTodayCount",
        "scheduled_later_today_count",
      ])
    ) {
      return [data]
    }
    const inner = data.data ?? data.rows ?? data.content ?? data.results
    if (Array.isArray(inner)) return inner
    if (inner && typeof inner === "object") return [inner]
  }
  return []
}

function normalizeDailyPostStatsRow(row) {
  const publishedCount = pickPostStatNumber(row, [
    "publishedCount",
    "published_count",
    "latestLivePosts",
    "latest_live_posts",
    "livePosts",
    "latestPublishedPosts",
    "publishedPosts",
  ])
  const publishedDifference = pickPostStatNumber(row, [
    "publishedDifference",
    "published_difference",
  ])
  const hasLiveCount = rowHasKey(row, ["currentlyLiveCount", "currently_live_count"])
  const currentlyLiveCount = hasLiveCount
    ? pickPostStatNumber(row, ["currentlyLiveCount", "currently_live_count"])
    : publishedCount
  const scheduledLaterTodayCount = pickPostStatNumber(row, [
    "scheduledLaterTodayCount",
    "scheduled_later_today_count",
    "latestScheduledPosts",
    "latest_scheduled_posts",
    "scheduledPosts",
    "scheduledLaterToday",
    "scheduled_later_today",
  ])
  const hasDifference = rowHasKey(row, ["publishedDifference", "published_difference"])
  const previousLivePosts = hasDifference
    ? publishedCount - publishedDifference
    : pickPostStatNumber(row, [
        "previousLivePosts",
        "previous_live_posts",
        "previousPublishedPosts",
      ])

  return {
    publishedCount,
    publishedDifference,
    currentlyLiveCount,
    scheduledLaterTodayCount,
    latestLivePosts: publishedCount,
    latestScheduledPosts: scheduledLaterTodayCount,
    previousLivePosts,
    previousScheduledPosts: pickPostStatNumber(row, [
      "previousScheduledPosts",
      "previous_scheduled_posts",
    ]),
    socialNetworkTypeId:
      row?.socialNetworkTypeId ?? row?.social_network_type_id ?? null,
  }
}

/**
 * @param {Array<object>} rows
 */
export function aggregateDailyPostStats(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      publishedCount: 0,
      publishedDifference: 0,
      currentlyLiveCount: 0,
      scheduledLaterTodayCount: 0,
      latestLivePosts: 0,
      latestScheduledPosts: 0,
      previousLivePosts: 0,
      previousScheduledPosts: 0,
    }
  }

  let publishedCount = 0
  let publishedDifference = 0
  let currentlyLiveCount = 0
  let scheduledLaterTodayCount = 0
  let previousLivePosts = 0
  let previousScheduledPosts = 0

  for (const row of rows) {
    const normalized = normalizeDailyPostStatsRow(row)
    publishedCount += normalized.publishedCount
    publishedDifference += normalized.publishedDifference
    currentlyLiveCount += normalized.currentlyLiveCount
    scheduledLaterTodayCount += normalized.scheduledLaterTodayCount
    previousLivePosts += normalized.previousLivePosts
    previousScheduledPosts += normalized.previousScheduledPosts
  }

  return {
    publishedCount,
    publishedDifference,
    currentlyLiveCount,
    scheduledLaterTodayCount,
    latestLivePosts: publishedCount,
    latestScheduledPosts: scheduledLaterTodayCount,
    previousLivePosts,
    previousScheduledPosts,
  }
}

function sparklineTrendFromValues(previous, latest) {
  if (latest > previous) return "up"
  if (latest < previous) return "down"
  return "stable"
}

export function buildCampaignPlatformRows(
  rows,
  socialNetworkTypes = [],
  connectedPlatformKeys = [],
) {
  const byPlatformKey = new Map()

  for (const row of Array.isArray(rows) ? rows : []) {
    const normalized = normalizeDailyPostStatsRow(row)
    const typeId = normalized.socialNetworkTypeId
    if (typeId == null) continue
    const platformKey = socialNetworkTypeIdToDashboardPlatformKey(typeId, socialNetworkTypes)
    if (!platformKey) continue
    const live = normalized.latestLivePosts
    const scheduled = normalized.latestScheduledPosts
    const existing = byPlatformKey.get(platformKey)
    if (existing) {
      existing.livePosts += live
      existing.scheduledPosts += scheduled
    } else {
      byPlatformKey.set(platformKey, {
        platform: platformKey,
        label: socialNetworkTypeIdToLabel(socialNetworkTypes, typeId),
        icon: PULSE_PLATFORM_ICON_PATHS[platformKey],
        livePosts: live,
        scheduledPosts: scheduled,
      })
    }
  }

  for (const platformKey of connectedPlatformKeys || []) {
    if (!platformKey || byPlatformKey.has(platformKey)) continue
    const typeId = catalogTypeIdFromKey(socialNetworkTypes, platformKey)
    byPlatformKey.set(platformKey, {
      platform: platformKey,
      label:
        typeId != null
          ? socialNetworkTypeIdToLabel(socialNetworkTypes, typeId)
          : platformKey,
      icon: PULSE_PLATFORM_ICON_PATHS[platformKey],
      livePosts: 0,
      scheduledPosts: 0,
    })
  }

  return Array.from(byPlatformKey.values()).sort((a, b) => b.livePosts - a.livePosts)
}

/** Merge API daily post stats into the Published Posts pulse card. */
export function buildCampaignCard(
  baseCard,
  rows,
  socialNetworkTypes = [],
  connectedPlatformKeys = [],
) {
  if (!baseCard) return baseCard

  const agg = aggregateDailyPostStats(rows ?? [])
  const {
    publishedCount,
    currentlyLiveCount,
    scheduledLaterTodayCount,
    previousLivePosts,
  } = agg

  const platformCampaignRows = buildCampaignPlatformRows(
    rows,
    socialNetworkTypes,
    connectedPlatformKeys,
  )
  const platformBreakdown = platformCampaignRows.reduce((acc, row) => {
    if (row.livePosts > 0) acc[row.platform] = row.livePosts
    return acc
  }, {})

  return {
    ...baseCard,
    title: baseCard.title || "Published Posts",
    subLabel: "Published",
    value: formatNumber(publishedCount),
    trophyInsight: `${formatNumber(currentlyLiveCount)} posts are currently live.`,
    platformBreakdown:
      Object.keys(platformBreakdown).length > 0 ? platformBreakdown : null,
    platformCampaignRows,
    sparklineData: [previousLivePosts, publishedCount],
    sparklineTrend: sparklineTrendFromValues(previousLivePosts, publishedCount),
    sparklinePercentChange: null,
    detailRows: [
      { label: "Published", value: formatNumber(publishedCount) },
      { label: "Scheduled Later Today", value: formatNumber(scheduledLaterTodayCount) },
    ],
  }
}
